import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import React, { FC, ReactElement, useRef, useCallback, useEffect } from 'react';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { mq } from 'styles/breakpoints';
import Arrow from './Arrow';
import { rem } from 'utils/fonts';

//region styled
const BottomSection = styled(Flex)`
  justify-content: center;
  ${mq.md} {
    justify-content: space-between;
    align-items: center;
  }
`;

const ArrowsWrapper = styled(Flex)`
  display: flex;
  flex-direction: row;
  margin-top: 5px;
  align-items: flex-end;
`;

//endregion

//region interfaces

interface Props {
  color?: string;
  settings?: Settings;
  initialSlide?: number;
  animatedGoto?: boolean;
}

const StyledDotsWrapper = styled.ul<{ color: string }>`
  & > li button::before {
    font-size: ${rem(10)} !important;
    color: ${(props): string => props.color} !important;
    opacity: 0.15 !important;
  }
  & .slick-active button::before {
    color: ${(props): string => props.color} !important;
    opacity: 0.5 !important;
  }
`;

//endregion

const Carousel: FC<Props> = ({ children, color = 'secondary.800', settings = {}, initialSlide, animatedGoto }) => {
  const ref = useRef<Slider>(null);

  const goToNextSlide = useCallback(() => {
    ref.current?.slickNext();
  }, []);

  const goToPrevSlide = useCallback(() => {
    ref.current?.slickPrev();
  }, []);

  const pauseSlider = useCallback(() => {
    ref.current?.slickPause();
  }, []);

  const playSlider = useCallback(() => {
    ref.current?.slickPlay();
  }, []);

  const gotoSlide = useCallback((index: number) => {
    ref.current?.slickGoTo(index, !animatedGoto);
  }, []);

  useEffect(() => {
    if (initialSlide != null) {
      gotoSlide(initialSlide);
    }
  }, [initialSlide]);

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    touchThreshold: 5,
    slidesPerRow: 1,
    arrows: false,
    autoplay: false,
    // eslint-disable-next-line react/display-name
    appendDots: (dots: ReactElement[]): ReactElement => {
      return (
        <BottomSection>
          <StyledDotsWrapper color={color}>{dots}</StyledDotsWrapper>
          <ArrowsWrapper>
            <Arrow onClick={goToPrevSlide} />
            <Arrow next onClick={goToNextSlide} />
          </ArrowsWrapper>
        </BottomSection>
      );
    },
    afterChange: (): void => {
      if (settings.autoplay) {
        pauseSlider();
        playSlider();
      }
    },
    ...settings,
  };

  return (
    <Slider ref={ref} {...carouselSettings}>
      {children}
    </Slider>
  );
};

export default Carousel;
